import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import api from '../api/client';

interface ClientBulkUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface BulkUploadResult {
  message: string;
  created_count: number;
  skipped_count: number;
  created_clients: string[];
  skipped_clients: string[];
}

export default function ClientBulkUpload({ open, onClose, onSuccess }: ClientBulkUploadProps) {
  const [clientNames, setClientNames] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);

  const handleUpload = async () => {
    if (!clientNames.trim()) {
      setError('Please enter at least one client name');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      // Split by newline or comma, trim whitespace, and filter out empty strings
      const names = clientNames
        .split(/[\n,]/)
        .map(name => name.trim())
        .filter(name => name.length > 0);

      const response = await api.bulkCreateClients(names);
      setUploadResult(response);
      setClientNames('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create clients');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setClientNames('');
    setError(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Add Clients</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {uploadResult && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadResult(null)}>
              {uploadResult.message}
              {uploadResult.created_count !== undefined && (
                <div>Created: {uploadResult.created_count} clients</div>
              )}
              {uploadResult.skipped_count !== undefined && (
                <div>Skipped: {uploadResult.skipped_count} clients</div>
              )}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Client Names"
            value={clientNames}
            onChange={(e) => setClientNames(e.target.value)}
            placeholder="Enter client names (separated by commas or new lines)"
            disabled={uploading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || !clientNames.trim()}
          startIcon={uploading ? <CircularProgress size={20} /> : undefined}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
